import Actions from './Actions';
import Content from './Content';
import HeadingGroup from './HeadingGroup';
import Root from './TemporaryDrawer';

export type { Props as TemporaryDrawerProps } from './TemporaryDrawer';
export type { Props as TemporaryDrawerActionsProps } from './Actions';
export type { Props as TemporaryDrawerContentProps } from './Content';
export type { Props as TemporaryDrawerHeadingGroupProps } from './HeadingGroup';

type RootType = typeof Root;

export interface TemporaryDrawerType extends RootType {
  /**
   * The TemporaryDrawer heading group component [Required]
   *
   * See [Props](__storybookUrl__/components-surfaces-temporarydrawer--default#props)
   *
   * @see TemporaryDrawer
   * @see TemporaryDrawer.Content
   * @see TemporaryDrawer.Actions
   */
  HeadingGroup: typeof HeadingGroup;

  /**
   * The TemporaryDrawer content component [Required]
   *
   * See [Props](__storybookUrl__/components-surfaces-temporarydrawer--default#props)
   *
   * @see TemporaryDrawer
   * @see TemporaryDrawer.HeadingGroup
   * @see TemporaryDrawer.Actions
   */
  Content: typeof Content;

  /**
   * The TemporaryDrawer actions component [Optional]
   *
   * See [Props](__storybookUrl__/components-surfaces-temporarydrawer--default#props)
   *
   * @see TemporaryDrawer
   * @see TemporaryDrawer.HeadingGroup
   * @see TemporaryDrawer.Content
   */
  Actions: typeof Actions;
}

/**
 * Temporary drawers are panels that slide in and out from the right or left over the page's main content.
 * They help to present context-specific information or provide a more in-depth interaction experience without leaving the page.
 *
 * See [Props](__storybookUrl__/components-surfaces-temporarydrawer--default#props)
 *
 *
 * Anatomy:
 *
 * ```jsx
 * <TemporaryDrawer>
 *   <TemporaryDrawer.HeadingGroup/>
 *   <TemporaryDrawer.Content />
 *   <TemporaryDrawer.Actions />
 * </TemporaryDrawer>
 * ```
 *
 * @see TemporaryDrawer.HeadingGroup
 * @see TemporaryDrawer.Content
 * @see TemporaryDrawer.Actions
 */
const TemporaryDrawer: TemporaryDrawerType = Object.assign(Root, {
  Actions,
  Content,
  HeadingGroup,
});

export { TemporaryDrawer };
